<template>
  <div class="store-shipping-services">
    <div class="store-shipping-services__header">
      <h4 class="store-shipping-services__title">Servicios de la tienda</h4>
      <AppButton variant="primary" @click="openCreateModal" :disabled="loading">
        + Agregar servicio
      </AppButton>
    </div>

    <AppTable
      :items="configs"
      :fields="fields"
      :busy="loading"
      :show-empty="true"
      empty-text="No hay servicios configurados para esta tienda."
      primary-key="id">
      <template #cell(service)="{ item }">
        {{ item.service ? item.service.code : '—' }}
      </template>
      <template #cell(name)="{ item }">
        {{ item.service ? item.service.name : '—' }}
      </template>
      <template #cell(region)="{ item }">
        {{ regionName(item) }}
      </template>
      <template #cell(isActive)="{ value }">
        <span :class="['store-shipping-services__badge', value ? 'is-active' : 'is-inactive']">
          {{ value ? 'Activo' : 'Inactivo' }}
        </span>
      </template>
      <template #cell(price)="{ value }">
        {{ formatPrice(value) }}
      </template>
      <template #cell(actions)="{ item }">
        <div class="store-shipping-services__actions">
          <AppIconButton
            icon="edit"
            title="Editar"
            @click="openEditModal(item)" />
          <AppIconButton
            icon="delete"
            title="Eliminar"
            danger
            @click="confirmDelete(item)" />
        </div>
      </template>
      <template #table-busy>
        <div class="store-shipping-services__loading">
          Cargando configuraciones...
        </div>
      </template>
    </AppTable>

    <StoreShippingServiceForm
      :value="createModalOpen"
      @input="createModalOpen = $event"
      :storeId="storeId"
      :services="services"
      :regions="regions"
      @saved="onSaved" />

    <StoreShippingServiceForm
      :value="editModalOpen"
      @input="editModalOpen = $event"
      :storeId="storeId"
      :config="selectedConfig"
      :services="services"
      :regions="regions"
      @saved="onSaved" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MakeToastMixin from '../mixins/MakeToastMixin.vue'
import AppButton from '../UI/AppButton.vue'
import AppTable from '../UI/AppTable.vue'
import AppIconButton from '../UI/AppIconButton.vue'
import StoreShippingServiceForm from './StoreShippingServiceForm.vue'

export default {
  name: 'StoreShippingServices',
  components: { AppButton, AppTable, AppIconButton, StoreShippingServiceForm },
  mixins: [MakeToastMixin],
  props: {
    storeId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      createModalOpen: false,
      editModalOpen: false,
      selectedConfig: null,
      fields: [
        { key: 'service', label: 'Código' },
        { key: 'name', label: 'Nombre' },
        { key: 'region', label: 'Región' },
        { key: 'isActive', label: 'Estado' },
        { key: 'price', label: 'Precio' },
        { key: 'actions', label: 'Acciones', thClass: 'store-shipping-services__actions-col' }
      ]
    }
  },
  computed: {
    ...mapGetters(['storeShippingConfigs', 'shippingServices', 'regions']),
    configs () {
      return Array.isArray(this.storeShippingConfigs) ? this.storeShippingConfigs : []
    },
    services () {
      return Array.isArray(this.shippingServices) ? this.shippingServices : []
    }
  },
  created () {
    this.fetchData()
  },
  watch: {
    storeId () {
      this.createModalOpen = false
      this.editModalOpen = false
      this.selectedConfig = null
      this.fetchData()
    }
  },
  methods: {
    ...mapActions(['fetchStoreShippingConfigs', 'fetchShippingServices', 'listRegion', 'deleteStoreShippingConfig']),
    async fetchData () {
      this.loading = true
      try {
        await Promise.all([
          this.fetchStoreShippingConfigs(this.storeId),
          this.fetchShippingServices({ includeInactive: true }),
          this.listRegion()
        ])
      } catch (err) {
        this.makeDangerToast('No se pudieron cargar los datos')
      } finally {
        this.loading = false
      }
    },
    openCreateModal () {
      this.selectedConfig = null
      this.createModalOpen = true
    },
    openEditModal (config) {
      this.selectedConfig = config
      this.editModalOpen = true
    },
    onSaved () {
      this.createModalOpen = false
      this.editModalOpen = false
      this.selectedConfig = null
      this.makeSuccessToast('Configuración guardada')
      this.fetchData()
    },
    async confirmDelete (config) {
      const serviceName = config.service ? config.service.name : (config.serviceId || '')
      const regionName = config.region ? config.region.name : this.regionName(config)
      const ok = await this.confirmModal.confirm(
        `¿Eliminar el servicio <strong>${serviceName}</strong> para la región <strong>${regionName}</strong>?<br>Esta acción no se puede deshacer.`,
        {
          title: 'Eliminar servicio de la tienda',
          confirmButton: {
            text: 'Eliminar',
            variant: 'danger'
          }
        }
      )
      if (!ok) return
      try {
        const response = await this.deleteStoreShippingConfig(config.id)
        if (response && response.status === 200) {
          this.makeSuccessToast('Servicio eliminado')
          await this.fetchData()
        } else if (response && response.data && response.data.error) {
          this.makeDangerToast(response.data.error)
        } else {
          this.makeDangerToast('No se pudo eliminar el servicio')
        }
      } catch (err) {
        this.makeDangerToast('Error al eliminar el servicio')
      }
    },
    formatPrice (price) {
      if (price === null || price === undefined || price === '') return '—'
      const num = Number(price)
      if (Number.isNaN(num)) return '—'
      return `$${num.toLocaleString('es-CL')}`
    },
    regionName (cfg) {
      if (cfg.region && cfg.region.name) return cfg.region.name
      const found = this.regions.find(r => r.id === cfg.regionId)
      return found ? found.name : '—'
    }
  }
}
</script>

<style scoped>
.store-shipping-services {
  width: 100%;
}

.store-shipping-services__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.store-shipping-services__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-color, #374151);
}

.store-shipping-services__loading {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.store-shipping-services__badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.store-shipping-services__badge.is-active {
  background: #d1fae5;
  color: #065f46;
}

.store-shipping-services__badge.is-inactive {
  background: #fee2e2;
  color: #991b1b;
}

.store-shipping-services__actions-col {
  width: 110px;
}

.store-shipping-services__actions {
  display: flex;
  gap: 0.5rem;
}
</style>
